import React, { useMemo, useState } from 'react'
import {
  Modal,
  Select,
  Alert,
  Space,
  Typography,
  Card,
  Divider,
  Row,
  Col,
  Tag,
  List,
} from 'antd'
import { MergeOutlined, ArrowRightOutlined } from '@ant-design/icons'

const { Text } = Typography

const getSettingNumber = (settings, key) => Number(settings?.[key] || 0)

const getDrinkLineTotal = (drinkDetails) => {
  if (!drinkDetails) return 0
  let details = drinkDetails
  try {
    details =
      typeof drinkDetails === 'string'
        ? JSON.parse(drinkDetails || '{}')
        : drinkDetails
  } catch {
    details = {}
  }

  const pegs = details?.pegs || []
  const pegQtys = details?.peg_qtys || {}
  const bottleQty = Number(details?.bottle_qty || 0)
  const bottlePrice = Number(details?.price_bottle || 0)

  const bottleTotal = bottleQty * bottlePrice
  const pegTotal = pegs.reduce((sum, peg) => {
    const qty = Number(pegQtys[String(peg.id)] || 0)
    return sum + qty * Number(peg.price || 0)
  }, 0)

  return bottleTotal + pegTotal
}

const getBillTotalLocal = (bill, generalSettings = {}, table = {}) => {
  const items = bill?.items || []

  const cgstTax = getSettingNumber(generalSettings, 'cgst_tax')
  const sgstTax = getSettingNumber(generalSettings, 'sgst_tax')
  const parcelCharge = getSettingNumber(generalSettings, 'parcel_charge')
  const additionalCharge = getSettingNumber(generalSettings, 'additional_charge')
  const acChargeSetting = getSettingNumber(generalSettings, 'ac_charge')
  const taxType = String(generalSettings?.tax_type || 'percentage').toLowerCase()

  const subtotal = items.reduce((sum, item) => {
    if (Number(item?.is_drink || 0) === 1 && item?.drink_details) {
      return sum + getDrinkLineTotal(item.drink_details)
    }
    return sum + (Number(item.price) || 0) * (Number(item.qty) || 0)
  }, 0)

  const discount = Number(bill?.discount || 0)

  const parcelQty = items.reduce((sum, item) => {
    if (Number(item.is_parcel || 0) === 1) {
      return sum + (Number(item.qty) || 0)
    }
    return sum
  }, 0)

  const parcelChargeTotal = parcelQty * parcelCharge
  const additionalChargeTotal = parcelQty * additionalCharge

  const isAcTable = String(table?.ac_type || '').toUpperCase() === 'AC'
  const acChargeTotal = isAcTable ? acChargeSetting : 0

  const taxableAmount =
    subtotal + parcelChargeTotal + additionalChargeTotal + acChargeTotal

 const cgstAmount = items.reduce(
  (sum, item) => sum + Number(item.item_cgst_amount || 0),
  0
)

const sgstAmount = items.reduce(
  (sum, item) => sum + Number(item.item_sgst_amount || 0),
  0
)

const tax = cgstAmount + sgstAmount

  const total = Math.max(
    0,
    subtotal +
      parcelChargeTotal +
      additionalChargeTotal +
      acChargeTotal -
      discount +
      tax
  )

  return {
    subtotal,
    discount,
    parcelQty,
    parcelChargeTotal,
    additionalChargeTotal,
    acChargeTotal,
    taxableAmount,
    cgstAmount,
    sgstAmount,
    tax,
    total,
  }
}

const BillSummaryCard = ({
  bill,
  index,
  label,
  color,
  generalSettings = {},
  table = {},
}) => {
  const items = bill?.items || []
  const totals = getBillTotalLocal(bill, generalSettings, table)

  return (
    <Card
      size="small"
      style={{
        borderColor: color,
        borderWidth: 2,
        borderRadius: 10,
        flex: 1,
      }}
      title={
        <Space>
          <Tag color={color} style={{ margin: 0 }}>
            {label}
          </Tag>
          <Text strong>Bill {index + 1}</Text>
        </Space>
      }
    >
      <Text type="secondary" style={{ fontSize: 12 }}>
        {items.length} item{items.length !== 1 ? 's' : ''}
      </Text>

      {items.length > 0 && (
        <List
          size="small"
          dataSource={items.slice(0, 3)}
          renderItem={(item) => (
            <List.Item style={{ padding: '2px 0', border: 'none' }}>
              <Text style={{ fontSize: 12 }}>
                {item.name} ×{item.qty}
                {Number(item.is_parcel || 0) === 1 ? ' (Parcel)' : ''}
              </Text>
            </List.Item>
          )}
        />
      )}

      {items.length > 3 && (
        <Text type="secondary" style={{ fontSize: 11 }}>
          +{items.length - 3} more…
        </Text>
      )}

      <Divider style={{ margin: '8px 0' }} />

      <Row justify="space-between" style={{ marginBottom: 4 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Subtotal
        </Text>
        <Text style={{ fontSize: 12 }}>₹{totals.subtotal.toFixed(2)}</Text>
      </Row>

      {totals.parcelChargeTotal > 0 && (
        <Row justify="space-between" style={{ marginBottom: 4 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Parcel Charge
          </Text>
          <Text style={{ fontSize: 12 }}>₹{totals.parcelChargeTotal.toFixed(2)}</Text>
        </Row>
      )}

      {totals.additionalChargeTotal > 0 && (
        <Row justify="space-between" style={{ marginBottom: 4 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Additional Charge
          </Text>
          <Text style={{ fontSize: 12 }}>
            ₹{totals.additionalChargeTotal.toFixed(2)}
          </Text>
        </Row>
      )}

      {totals.acChargeTotal > 0 && (
        <Row justify="space-between" style={{ marginBottom: 4 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            AC Charge
          </Text>
          <Text style={{ fontSize: 12 }}>₹{totals.acChargeTotal.toFixed(2)}</Text>
        </Row>
      )}

      {totals.discount > 0 && (
        <Row justify="space-between" style={{ marginBottom: 4 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Discount
          </Text>
          <Text style={{ fontSize: 12, color: '#ef4444' }}>
            -₹{totals.discount.toFixed(2)}
          </Text>
        </Row>
      )}

      <Row justify="space-between" style={{ marginBottom: 6 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Tax
        </Text>
        <Text style={{ fontSize: 12 }}>₹{totals.tax.toFixed(2)}</Text>
      </Row>

      <Divider style={{ margin: '8px 0' }} />

      <Text strong style={{ color: '#f59e0b' }}>
        ₹{totals.total.toFixed(2)}
      </Text>
    </Card>
  )
}

const MergeBillsModalNoContext = ({
  tableId,
  bills = [],
  activeKey,
  onClose,
  onMergeBills,
  generalSettings = {},
  table = {},
}) => {
  const defaultSource = activeKey ?? bills[0]?.id
  const defaultTarget = bills.find((b) => b.id !== defaultSource)?.id ?? null

  const [sourceBillId, setSourceBillId] = useState(defaultSource)
  const [targetBillId, setTargetBillId] = useState(defaultTarget)
  const [merging, setMerging] = useState(false)

  const sourceBill = useMemo(
    () => bills.find((b) => b.id === sourceBillId),
    [bills, sourceBillId]
  )

  const targetBill = useMemo(
    () => bills.find((b) => b.id === targetBillId),
    [bills, targetBillId]
  )

  const sourceOptions = bills.map((b, idx) => ({
    label: `Bill ${idx + 1} — ₹${getBillTotalLocal(
      b,
      generalSettings,
      table
    ).total.toFixed(0)} (${b.items?.length ?? 0} items)`,
    value: b.id,
    disabled: b.id === targetBillId,
  }))

  const targetOptions = bills.map((b, idx) => ({
    label: `Bill ${idx + 1} — ₹${getBillTotalLocal(
      b,
      generalSettings,
      table
    ).total.toFixed(0)} (${b.items?.length ?? 0} items)`,
    value: b.id,
    disabled: b.id === sourceBillId,
  }))

  const handleSourceChange = (val) => {
    setSourceBillId(val)
    if (val === targetBillId) {
      const other = bills.find((b) => b.id !== val)
      setTargetBillId(other?.id ?? null)
    }
  }

  const handleTargetChange = (val) => {
    setTargetBillId(val)
    if (val === sourceBillId) {
      const other = bills.find((b) => b.id !== val)
      setSourceBillId(other?.id ?? null)
    }
  }

  const handleMerge = async () => {
    if (!sourceBillId || !targetBillId || sourceBillId === targetBillId) return

    setMerging(true)
    try {
      await onMergeBills(sourceBillId, targetBillId)
    } finally {
      setMerging(false)
    }
  }

  const canMerge =
    sourceBillId && targetBillId && String(sourceBillId) !== String(targetBillId)

  const sourceIndex = bills.findIndex((b) => b.id === sourceBillId)
  const targetIndex = bills.findIndex((b) => b.id === targetBillId)

  const sourceTotals = sourceBill
    ? getBillTotalLocal(sourceBill, generalSettings, table)
    : null
  const targetTotals = targetBill
    ? getBillTotalLocal(targetBill, generalSettings, table)
    : null

  return (
    <Modal
      title={
        <Space>
          <MergeOutlined style={{ color: '#6366f1' }} />
          Merge Bills
        </Space>
      }
      open={true}
      onCancel={onClose}
      onOk={handleMerge}
      okText={merging ? 'Merging…' : 'Merge Now'}
      okButtonProps={{
        disabled: !canMerge,
        loading: merging,
        style: canMerge
          ? { background: '#6366f1', borderColor: '#6366f1' }
          : {},
      }}
      cancelButtonProps={{ disabled: merging }}
      width={520}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        <Alert
          message="Items from the source bill will be combined into the target bill. The source bill will be deleted."
          type="info"
          showIcon
          style={{ borderRadius: 8 }}
        />

        <div>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>
            Source Bill{' '}
            <Text type="secondary" style={{ fontWeight: 400 }}>
              (will be merged away)
            </Text>
          </Text>
          <Select
            style={{ width: '100%' }}
            options={sourceOptions}
            value={sourceBillId}
            onChange={handleSourceChange}
            size="large"
            placeholder="Select source bill"
          />
        </div>

        <div>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>
            Target Bill{' '}
            <Text type="secondary" style={{ fontWeight: 400 }}>
              (items will be added here)
            </Text>
          </Text>
          <Select
            style={{ width: '100%' }}
            options={targetOptions}
            value={targetBillId}
            onChange={handleTargetChange}
            size="large"
            placeholder="Select target bill"
          />
        </div>

        {sourceBill && targetBill && sourceTotals && targetTotals && (
          <>
            <Divider style={{ margin: '4px 0' }}>Preview</Divider>

            <Row gutter={8} align="middle">
              <Col flex="1">
                <BillSummaryCard
                  bill={sourceBill}
                  index={sourceIndex}
                  label="FROM"
                  color="orange"
                  generalSettings={generalSettings}
                  table={table}
                />
              </Col>

              <Col style={{ textAlign: 'center', padding: '0 4px' }}>
                <ArrowRightOutlined
                  style={{ fontSize: 20, color: '#6366f1' }}
                />
              </Col>

              <Col flex="1">
                <BillSummaryCard
                  bill={targetBill}
                  index={targetIndex}
                  label="INTO"
                  color="blue"
                  generalSettings={generalSettings}
                  table={table}
                />
              </Col>
            </Row>

            <Card
              size="small"
              style={{
                background: '#f0fdf4',
                borderColor: '#86efac',
                borderRadius: 10,
              }}
            >
              <Row justify="space-between" align="middle">
                <Col>
                  <Text type="secondary">Combined Total</Text>
                </Col>
                <Col>
                  <Text strong style={{ fontSize: 18, color: '#10b981' }}>
                    ₹{(sourceTotals.total + targetTotals.total).toFixed(2)}
                  </Text>
                </Col>
              </Row>

              <Row justify="space-between" style={{ marginTop: 4 }}>
                <Col>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Combined items
                  </Text>
                </Col>
                <Col>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {(sourceBill.items?.length ?? 0) + (targetBill.items?.length ?? 0)}{' '}
                    items
                  </Text>
                </Col>
              </Row>
            </Card>
          </>
        )}
      </Space>
    </Modal>
  )
}

export default MergeBillsModalNoContext
